import { ContractFileType } from '@shared/models/contract';

import { BookingV2PersonKind } from '../dtos/booking-v2.dtos';

/**
 * Everything the contract dialog collects, in a backend-agnostic shape. The
 * `workflows/contract-creation` endpoint re-hydrates person/company/boat/berth
 * from their `id` (see contract-creation.ts), so only IDs are sent here.
 *
 * The `*Form` blocks and `unknownShortCodes` mirror the legacy ContractCreator
 * form-widget output 1:1 and are filled by the later slices (co-owner, BKZ,
 * products, signature, unknown placeholders); they default to omitted.
 */
export interface BookingV2ContractSelection {
    preview: boolean;
    bookingId: number | null;
    contractId: number;
    person: { id: number; kind: BookingV2PersonKind };
    boatId: number | null;
    berthId: number | null;
    fromDate: Date | null;
    toDate: Date | null;
    sendMail: boolean;
    downloadPdf: boolean;
    contractFileType: ContractFileType;
    contractCoOwnerForm?: Record<string, unknown>;
    contractBuildingCostSubsidyForm?: Record<string, unknown>;
    productsForm?: { products: Array<{ product: unknown; quantity: unknown }> };
    unknownShortCodes?: Record<string, string>;
    sign?: Record<string, unknown>;
}

export interface ContractCoOwner {
    name: string;
    str: string;
    plz: string;
    ort: string;
}

/** Maps up to 4 co-owner rows to the flat `VMI_<n>_*` shape the backend expects. */
export function buildCoOwnerForm(coOwners: readonly ContractCoOwner[]): Record<string, string | null> {
    const out: Record<string, string | null> = {};
    coOwners.slice(0, 4).forEach((c, idx) => {
        const n = idx + 1;
        out[`VMI_${n}_NAME_KOMPLETT`] = c.name?.trim() || null;
        out[`VMI_${n}_STR`] = c.str?.trim() || null;
        out[`VMI_${n}_PLZ`] = c.plz?.trim() || null;
        out[`VMI_${n}_ORT`] = c.ort?.trim() || null;
    });
    return out;
}

/**
 * Assembles the exact request body the legacy ContractCreator sent to
 * `POST workflows/contract-creation` — including `contractFileType` as the enum
 * *name* string (`ContractFileType[value]`), which is what the backend parses.
 */
export function buildContractCreationBody(sel: BookingV2ContractSelection): Record<string, unknown> {
    const body: Record<string, unknown> = {
        preview: sel.preview,
        bcm_bookings_id: sel.bookingId ?? undefined,
        person: sel.person.kind === 'person' ? { id: sel.person.id } : null,
        company: sel.person.kind === 'company' ? { id: sel.person.id } : null,
        boat: sel.boatId ? { id: sel.boatId } : null,
        berth: sel.berthId ? { id: sel.berthId } : null,
        contract: { id: sel.contractId },
        sendMail: sel.sendMail,
        downloadPdf: sel.downloadPdf,
        contractFileType: ContractFileType[sel.contractFileType],
        fromDate: sel.fromDate,
        toDate: sel.toDate,
        contractCoOwnerForm: sel.contractCoOwnerForm,
        contractBuildingCostSubsidyForm: sel.contractBuildingCostSubsidyForm,
        productsForm: sel.productsForm,
        unknownShortCodes: sel.unknownShortCodes ?? {},
    };
    if (sel.sign) {
        body.sign = sel.sign;
    }
    return body;
}
